/**
 * Extracts admission number from CSV row object
 * Handles various column name variations
 * @param {Object} row - CSV row object
 * @returns {string|null} Admission number or null if not found
 */
export const extractAdmissionNumber = (row) => {
    if (!row || typeof row !== 'object') {
        return null;
    }
    
    // Define possible admission number field names (lowercase, normalized)
    const normalizedAdmissionFields = [
        'admissionno',
        'admissionnumber', 
        'studentno',
        'studentnumber',
        'adno',
        'admnno'
    ];
    
    // Check each field in the row
    for (const [key, value] of Object.entries(row)) {
        // Normalize the field name: lowercase, remove spaces, underscores, dots
        const normalizedKey = key.toLowerCase().replace(/[_\s\.]/g, '');
        // Check if it matches any of our admission field patterns
        if (normalizedAdmissionFields.includes(normalizedKey)) {
            if (value && value.toString().trim()) {
                return value.toString().trim();
            }
        }
    }
    
    return null;
}

export const extractVoucherNo = (row) => {
    if (!row || typeof row !== 'object') {
        return null;
    }
    
    // Define possible voucher/bill number field names (lowercase, normalized)
    const normalizedVoucherFields = [
        'voucherno',
        'vouchernumber',
        'billno',
        'billnumber',
        'receiptno',
        'receiptnumber',
        'invoiceno',
        'invoicenumber',
        'transactionno',
        'transactionnumber',
        'paymentno',
        'paymentnumber',
        'referenceno',
        'referencenumber'
    ];
    
    // Check each field in the row
    for (const [key, value] of Object.entries(row)) {
        // Normalize the field name: lowercase, remove spaces, underscores, dots
        const normalizedKey = key.toLowerCase().replace(/[_\s\.]/g, '');
        
        // Check if it matches any of our voucher field patterns
        if (normalizedVoucherFields.includes(normalizedKey)) {
            if (value && value.toString().trim()) {
                return value.toString().trim();
            }
        }
    }
    
    return null;
}

/**
 * Extracts amount from CSV row object
 * Handles various column name variations
 * @param {Object} row - CSV row object
 * @returns {string|null} Amount or null if not found
 */
export const extractAmount = (row) => {
    if (!row || typeof row !== 'object') {
        return null;
    }
    
    // Define possible amount field names (lowercase, normalized)
    const normalizedAmountFields = [
        'amount',
        'totalamount',
        'fee',
        'feeamount',
        'dueamount',
        'due',
        'pendingamount',
        'pending',
        'total'
    ];
    
    // Check each field in the row
    for (const [key, value] of Object.entries(row)) {
        // Normalize the field name: lowercase, remove spaces, underscores, dots
        const normalizedKey = key.toLowerCase().replace(/[_\s\.]/g, '');
        
        // Check if it matches any of our amount field patterns
        if (normalizedAmountFields.includes(normalizedKey)) {
            if (value && value.toString().trim()) {
                // Clean the amount value - remove currency symbols and commas
                const cleanedValue = value.toString().trim().replace(/[₹$,]/g, '');
                return cleanedValue;
            }
        }
    }
    
    return null;
}

/**
 * Extracts due particulars from CSV row object
 * Handles various column name variations
 * @param {Object} row - CSV row object
 * @returns {string|null} Due particulars or null if not found
 */
export const extractDueParticulars = (row) => {
    if (!row || typeof row !== 'object') {
        return null;
    }
    
    // Define possible due particulars field names (lowercase, normalized)
    const normalizedParticularsFields = [
        'particulars',
        'dueparticulars',
        'feeheads',
        'feeheading',
        'description',
        'details',
        'feetype',
        'type',
        'category'
    ];
    
    // Check each field in the row
    for (const [key, value] of Object.entries(row)) {
        // Normalize the field name: lowercase, remove spaces, underscores, dots
        const normalizedKey = key.toLowerCase().replace(/[_\s\.]/g, '');
        
        // Check if it matches any of our particulars field patterns
        if (normalizedParticularsFields.includes(normalizedKey)) {
            if (value && value.toString().trim()) {
                return value.toString().trim();
            }
        }
    }
    
    return null;
}

